/**
 * MER-605: manifest `reference_lines` -> chart-space horizontal lines.
 *
 * `reference_lines` has been in the manifest schema since the v8.3 build
 * (src/meridian_pipeline/dashboard/manifest.py `ReferenceLine`) and three
 * widgets declare one: 1.4.2 Disposal Fee Recovery Ratio carries Heath's
 * break-even line at 100, and L4.2b All Routes Ranked carries the 30%
 * contribution-margin floor. Nothing in dashboard/src ever read the field,
 * so no line has ever been drawn. This module is the missing half: it turns
 * the declared lines into the numbers Chart.js needs, and Chart.tsx draws
 * them.
 *
 * UNITS. A reference line is authored in DISPLAY units, the same number the
 * y-axis tick prints. Percent columns are stored as FRACTIONS (house
 * convention, MER-184: 0.42 renders "42.0%") and Chart.tsx's percent tick
 * callback multiplies by 100, so a percent chart's data space is 0..1 while
 * its axis reads 0%..100%. Drawing Heath's `value: 100` raw would put the
 * break-even line a hundred times above every bar and flatten the series
 * into the baseline. Dividing by 100 lands the line exactly on the gridline
 * the axis labels "100%", which is what "break-even at 100%" means. Every
 * other format (currency, integer, decimal, ratio) stores and displays the
 * same number, so it passes through untouched.
 */

/** Formats whose stored data units differ from their display units. */
const DISPLAY_UNIT_DIVISOR = {
  percent: 100,
};

/**
 * Resolve a widget's declared `reference_lines` into chart data space.
 *
 * Defensive by design: manifests are YAML parsed at runtime and cast, so a
 * malformed entry must be dropped rather than crash the chart or draw a
 * line at NaN. Anything without a finite numeric `value` is skipped.
 *
 * @param {unknown} declared  the raw `widget.reference_lines` value
 * @param {string|undefined} format  the chart's resolved KpiFormat
 * @returns {{value: number, label: string}[]} lines in chart data units
 */
export function resolveReferenceLines(declared, format) {
  if (!Array.isArray(declared)) return [];
  const divisor = DISPLAY_UNIT_DIVISOR[format] ?? 1;
  const out = [];
  for (const line of declared) {
    if (!line || typeof line !== "object") continue;
    const raw =
      typeof line.value === "number"
        ? line.value
        : typeof line.value === "string"
          ? Number(line.value)
          : NaN;
    if (!Number.isFinite(raw)) continue;
    out.push({
      value: raw / divisor,
      label: typeof line.label === "string" ? line.label : "",
    });
  }
  return out;
}

/**
 * Y-axis bounds that keep every reference line inside the plot area.
 *
 * Chart.js sizes the y scale from the DATA alone, so a break-even line at
 * 100% sits off-canvas whenever recovery runs under 100% for the whole
 * window, which is precisely the month an operator needs to see the line.
 * `suggestedMin` / `suggestedMax` widen the scale only when the data does
 * not already reach that far, so real data always keeps its own range.
 *
 * @param {{value: number}[]} lines  resolved lines (chart data units)
 * @returns {{suggestedMin: number, suggestedMax: number}|null}
 */
export function referenceLineBounds(lines) {
  if (!Array.isArray(lines) || lines.length === 0) return null;
  const values = lines.map((l) => l?.value).filter((v) => Number.isFinite(v));
  if (values.length === 0) return null;
  return { suggestedMin: Math.min(...values), suggestedMax: Math.max(...values) };
}

/**
 * A target line is annotation, not a series. Slate keeps it legible over any
 * dataset without reading as another measure or as an alert (the red / amber
 * in ChartWidget's NAMED_COLORS carry status meaning).
 */
export const REFERENCE_LINE_COLOR = "#5D6D7E";

/** Reference-line label type size, matching the axis ticks. */
const LABEL_FONT_PX = 10;
/** Space the label needs above the line before it is flipped underneath. */
const LABEL_HEADROOM_PX = LABEL_FONT_PX + 3;

/**
 * Chart.js plugin that draws a widget's reference lines.
 *
 * Attached per chart instance rather than globally registered, so it closes
 * over this chart's own lines and a chart with none pays nothing.
 * `afterDatasetsDraw` puts the line on top of the bars, where a target
 * belongs. Values are already in chart data units.
 *
 * Lives here, not in Chart.tsx, so node --test can drive the draw path with
 * a recording 2d context instead of it being provable only by eye.
 *
 * @param {{value: number, label: string}[]} lines  resolved lines
 * @param {string} fontStack  the chart's font stack, for the line label
 */
export function referenceLinePlugin(lines, fontStack) {
  return {
    id: "meridianReferenceLines",
    afterDatasetsDraw(chart) {
      const yScale = chart?.scales?.y;
      const area = chart?.chartArea;
      const ctx = chart?.ctx;
      if (!yScale || !area || !ctx) return;
      ctx.save();
      for (const line of lines) {
        const y = yScale.getPixelForValue(line.value);
        // Defensive: an unscaled axis, or a line still outside the plot
        // (a widget-level `options.scales.y.max` overrides the suggested
        // bounds), must not paint over the axes or the legend.
        if (!Number.isFinite(y) || y < area.top || y > area.bottom) continue;
        ctx.beginPath();
        ctx.setLineDash([6, 4]);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = REFERENCE_LINE_COLOR;
        ctx.moveTo(area.left, y);
        ctx.lineTo(area.right, y);
        ctx.stroke();
        if (line.label) {
          // The label normally sits just above the line. But a line at the
          // TOP of the scale is the common case, not the rare one: the y
          // scale is widened to reach it, so whenever the data stays under
          // the target (recovery below break-even, every route under the
          // 30% floor) the line lands on area.top and a label drawn above
          // it leaves the plot entirely and collides with the legend.
          // Verified in headless Chromium before this branch existed: the
          // "Break-even" text painted at canvas rows 0 to 7, above a
          // chartArea starting at 10.2. Flip it under the line instead.
          const above = y - area.top >= LABEL_HEADROOM_PX;
          ctx.setLineDash([]);
          ctx.font = `600 ${LABEL_FONT_PX}px ${fontStack}`;
          ctx.fillStyle = REFERENCE_LINE_COLOR;
          ctx.textAlign = "right";
          ctx.textBaseline = above ? "bottom" : "top";
          ctx.fillText(line.label, area.right - 4, above ? y - 3 : y + 3);
        }
      }
      ctx.restore();
    },
  };
}
